import React, { useState } from 'react';
import { Input, Select, Button, message, InputNumber, Upload, Tooltip } from 'antd';
import { UploadOutlined, UserAddOutlined, InfoCircleOutlined } from '@ant-design/icons';
import MultiSelect from '../InputComponents/MultiSelect';
import dayjs from 'dayjs';



const { Option } = Select;

const companyOptions = [
  { value: 'company_a', label: 'Company A' },
  { value: 'company_b', label: 'Company B' },
  { value: 'company_c', label: 'Company C' }
];

const AutomaticDataEntryForPlanParticipants = ({ onAddRow }) => {
  const [formData, setFormData] = useState({
    companyName: [],
    participantFirstName: '',
    participantLastName: '',
    riskToleranceScore: null,
    riskToleranceReport: null,
    hasFinancialAdvisor: 'no',
    lookingForFinancialAdvisor: 'no',
    age: null,
    amountInvested: null
  });

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (info) => {
    if (info.file.status === 'done') {
      message.success(`${info.file.name} file uploaded successfully`);
      setFormData(prev => ({ ...prev, riskToleranceReport: info.file }));
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  const handleSubmit = () => {
    const newRow = {
      CompanyName: formData.companyName.join(', '),
      ParticipantFirstName: formData.participantFirstName,
      ParticipantLastName: formData.participantLastName,
      RiskToleranceScore: formData.riskToleranceScore,
      RiskToleranceReport: formData.riskToleranceReport ? formData.riskToleranceReport.name : '',
      HasFinancialAdvisor: formData.hasFinancialAdvisor,
      LookingForFinancialAdvisor: formData.lookingForFinancialAdvisor,
      Age: formData.age,
      AmountInvested: formData.amountInvested
    };

    onAddRow(newRow);
    message.success('Client prospecting data submitted successfully');
    
    // Reset form after submission
    setFormData({
      companyName: [],
      participantFirstName: '',
      participantLastName: '',
      riskToleranceScore: null,
      riskToleranceReport: null,
      hasFinancialAdvisor: 'no',
      lookingForFinancialAdvisor: 'no',
      age: null,
      amountInvested: null
    });
  };

  const isFormValid = () => {
    return formData.companyName.length > 0 &&
           formData.participantFirstName &&
           formData.participantLastName &&
           formData.riskToleranceScore !== null &&
           formData.age !== null &&
           formData.amountInvested !== null;
  };

  const renderLabel = (label, tooltip) => (
    <div className="flex items-center">
      <span>{label}</span>
      <Tooltip title={tooltip}>
        <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
      </Tooltip>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-md">
      <h1 className="text-2xl font-bold mb-6">Plan Participants Data Entry</h1>
      <form className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Company Name", "Select the company or companies this participant is associated with.")}
            </label>
            <MultiSelect
              options={companyOptions}
              selectedValues={formData.companyName}
              onChange={(value) => handleChange('companyName', value)}
              placeholder="Select company(s)"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Participant First Name", "Enter the participant's legal first name.")}
            </label>
            <Input
              value={formData.participantFirstName}
              onChange={(e) => handleChange('participantFirstName', e.target.value)}
              placeholder="Enter first name"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Participant Last Name", "Enter the participant's legal last name.")}
            </label>
            <Input
              value={formData.participantLastName}
              onChange={(e) => handleChange('participantLastName', e.target.value)}
              placeholder="Enter last name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Risk Tolerance Score", "Enter the participant's risk assessment score (0-100).")}
            </label>
            <InputNumber
              value={formData.riskToleranceScore}
              onChange={(value) => handleChange('riskToleranceScore', value)}
              min={0}
              max={100}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {renderLabel("Risk Tolerance Report", "Upload the participant's complete risk assessment report.")}
          </label>
          <Upload
            onChange={handleFileUpload}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Upload Report</Button>
          </Upload>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Has Financial Advisor", "Indicate if the participant currently works with a financial advisor.")}
            </label>
            <Select
              value={formData.hasFinancialAdvisor}
              onChange={(value) => handleChange('hasFinancialAdvisor', value)}
              style={{ width: '100%' }}
            >
              <Option value="yes">Yes</Option>
              <Option value="no">No</Option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Looking for Financial Advisor", "Indicate if the participant is interested in financial advisory services.")}
            </label>
            <Select
              value={formData.lookingForFinancialAdvisor}
              onChange={(value) => handleChange('lookingForFinancialAdvisor', value)}
              style={{ width: '100%' }}
            >
              <Option value="yes">Yes</Option>
              <Option value="no">No</Option>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Age", "Enter the participant's current age.")}
            </label>
            <InputNumber
              value={formData.age}
              onChange={(value) => handleChange('age', value)}
              min={0}
              max={120}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Amount Invested", "Total amount currently invested in the plan.")}
            </label>
            <InputNumber
              value={formData.amountInvested}
              onChange={(value) => handleChange('amountInvested', value)}
              min={0}
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div className="flex justify-start">
          <Tooltip title="Save this participant's information to the database">
            <Button 
              onClick={handleSubmit}
              disabled={!isFormValid()}
              size="large"
              className={`inline-flex items-center px-6 py-3 border border-transparent text-lg font-medium rounded-lg shadow-sm text-white ${isFormValid() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out`}
            >
              <UserAddOutlined className="h-5 w-5 mr-2" />
              Submit Client Data
            </Button>
          </Tooltip>
        </div>
      </form>
    </div>
  );
};

export default AutomaticDataEntryForPlanParticipants;

